import { t } from "@lingui/macro";
import {
	auth,
	GoogleAuthProvider,
	getRedirectResult,
	hasAdminAccess,
	linkGoogleWithPassword,
	sendPasswordReset,
	signInWithGoogle,
} from "@repo/ui";
import * as Sentry from "@sentry/react";
import type { FirebaseError } from "firebase/app";
import type { AuthCredential, User as FirebaseUser } from "firebase/auth";
import { AlertCircle, CheckCircle2, Languages, Loader2 } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import { Button } from "./ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "./ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";

function emailSignInErrorMessage(error: unknown): string {
	const code =
		error && typeof error === "object" && "code" in error ? error.code : null;
	if (
		code === "auth/invalid-credential" ||
		code === "auth/wrong-password" ||
		code === "auth/user-not-found" ||
		code === "auth/invalid-email"
	) {
		return t({
			id: "loginInvalidCredentials",
			message: "Incorrect email or password.",
		});
	}
	if (
		code === "auth/network-request-failed" ||
		code === "auth/too-many-requests"
	) {
		return t({
			id: "loginTemporarilyUnavailable",
			message: "Sign-in temporarily unavailable. Please try again later.",
		});
	}
	return t({
		id: "unableToSignIn",
		message: "Unable to sign in. Please try again.",
	});
}

const processAuthenticatedUser = async (userObj: FirebaseUser) => {
	const tokenResult = await userObj.getIdTokenResult(true);
	const companyId = tokenResult.claims.companyId as string | undefined;
	const isAdmin = hasAdminAccess(tokenResult.claims);

	if (!companyId && !isAdmin) {
		window.history.pushState({}, "", "/register");
		window.dispatchEvent(new PopStateEvent("popstate"));
		return;
	}
};

export function LoginPage() {
	const { login, language, setLanguage } = useData();
	const [email, setEmail] = useState("");
	const emailRef = useRef(email);
	emailRef.current = email;
	const [password, setPassword] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [isGoogleLoading, setIsGoogleLoading] = useState(false);
	const [error, setError] = useState("");
	const [infoMessage, setInfoMessage] = useState("");

	// Account linking modal state
	const [showLinkModal, setShowLinkModal] = useState(false);
	const [linkPassword, setLinkPassword] = useState("");
	const [pendingEmail, setPendingEmail] = useState("");
	const [pendingCredential, setPendingCredential] =
		useState<AuthCredential | null>(null);
	const [isLinking, setIsLinking] = useState(false);

	// Forgot password modal state
	const [showForgotModal, setShowForgotModal] = useState(false);
	const [forgotEmail, setForgotEmail] = useState("");
	const [isSendingReset, setIsSendingReset] = useState(false);

	// Apply RTL when Hebrew is selected
	useEffect(() => {
		document.documentElement.dir = language === "he" ? "rtl" : "ltr";
		document.documentElement.lang = language;
	}, [language]);

	const handleAccountCollisionError = useCallback((err: FirebaseError) => {
		const userEmail =
			typeof err.customData?.email === "string"
				? err.customData.email
				: emailRef.current;
		const cred = GoogleAuthProvider.credentialFromError(err);
		setPendingEmail(userEmail);
		setPendingCredential(cred);
		setShowLinkModal(true);
	}, []);
	// Handle redirect result if user was redirected for Google Sign-In
	useEffect(() => {
		getRedirectResult(auth)
			.then(async (result) => {
				if (result?.user) {
					await processAuthenticatedUser(result.user);
				}
			})
			.catch((err) => {
				if (
					(err as FirebaseError | null)?.code ===
					"auth/account-exists-with-different-credential"
				) {
					handleAccountCollisionError(err as FirebaseError);
				}
			});
	}, [handleAccountCollisionError]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsLoading(true);
		setError("");
		setInfoMessage("");

		try {
			await login(email, password);
		} catch (err) {
			console.error(err);
			Sentry.captureException(err);
			setError(emailSignInErrorMessage(err));
		} finally {
			setIsLoading(false);
		}
	};

	const handleGoogleSignIn = async () => {
		setIsGoogleLoading(true);
		setError("");
		setInfoMessage("");

		try {
			const userCredential = await signInWithGoogle();
			if (!userCredential) return; // User was redirected
			await processAuthenticatedUser(userCredential.user);
		} catch (err: unknown) {
			if (
				(err as FirebaseError | null)?.code ===
				"auth/account-exists-with-different-credential"
			) {
				handleAccountCollisionError(err as FirebaseError);
			} else if (
				(err as FirebaseError | null)?.code !== "auth/popup-closed-by-user"
			) {
				console.error(err);
				setError(
					t({
						id: "googleSignInFailed",
						message: "Google sign in failed. Please try again.",
					}),
				);
			}
		} finally {
			setIsGoogleLoading(false);
		}
	};

	const handleLinkAccountSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!pendingEmail || !pendingCredential || !linkPassword) return;

		setIsLinking(true);
		setError("");
		try {
			await linkGoogleWithPassword(
				pendingEmail,
				linkPassword,
				pendingCredential,
			);
			setShowLinkModal(false);
			setLinkPassword("");
			toast.success(
				t({
					id: "accountLinkedSuccess",
					message: "Google account successfully linked!",
				}),
			);
		} catch (err: unknown) {
			console.error("Failed to link account", err);
			setError(
				t({
					id: "linkingFailed",
					message: "Failed to link account. Please check password.",
				}),
			);
		} finally {
			setIsLinking(false);
		}
	};

	const handleSendForgotPassword = async (emailToSend: string) => {
		if (!emailToSend) return;
		setIsSendingReset(true);
		try {
			await sendPasswordReset(emailToSend);
			toast.success(
				t({
					id: "resetEmailSentToast",
					message: "Password reset link sent to your email.",
				}),
			);
			setInfoMessage(
				t({
					id: "resetEmailSentMessage",
					message: "Check your inbox for password reset instructions.",
				}),
			);
			setShowForgotModal(false);
		} catch (err: unknown) {
			console.error("Failed to send password reset email", err);
			toast.error(
				t({
					id: "resetEmailFailed",
					message: "Failed to send password reset email.",
				}),
			);
		} finally {
			setIsSendingReset(false);
		}
	};

	const navigateToRegister = () => {
		window.history.pushState({}, "", "/register");
		window.dispatchEvent(new PopStateEvent("popstate"));
	};

	return (
		<div className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
			<Card className="w-full max-w-md">
				<CardHeader className="text-center">
					<div className="flex justify-end mb-2">
						<Select
							value={language}
							onValueChange={(value: "en" | "he") => setLanguage(value)}
						>
							<SelectTrigger className="w-[140px]">
								<Languages
									className={`w-4 h-4 ${language === "he" ? "ml-2" : "mr-2"}`}
								/>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="en">
									{t({ id: "english", message: "English" })}
								</SelectItem>
								<SelectItem value="he">
									{t({ id: "hebrew", message: "Hebrew (עברית)" })}
								</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<img
						src="/provoize_icon.svg"
						alt="Provoize"
						className="mx-auto mb-4 h-12 w-12"
					/>
					<CardTitle className="text-2xl">
						{t({ id: "appName", message: "Provoize" })}
					</CardTitle>
					<CardDescription>
						{t({
							id: "loginSubtitle",
							message: "Sign in to manage your agents",
						})}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						<Button
							type="button"
							variant="outline"
							className="w-full flex items-center justify-center gap-2 py-5"
							onClick={handleGoogleSignIn}
							disabled={isGoogleLoading || isLoading}
						>
							{isGoogleLoading ? (
								<Loader2 className="w-4 h-4 animate-spin" />
							) : (
								<svg className="w-5 h-5" viewBox="0 0 24 24">
									<path
										fill="#4285F4"
										d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
									/>
									<path
										fill="#34A853"
										d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
									/>
									<path
										fill="#FBBC05"
										d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
									/>
									<path
										fill="#EA4335"
										d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
									/>
								</svg>
							)}
							<span>
								{t({ id: "signInWithGoogle", message: "Sign in with Google" })}
							</span>
						</Button>

						<div className="relative my-4">
							<div className="absolute inset-0 flex items-center">
								<div className="w-full border-t border-border" />
							</div>
							<div className="relative flex justify-center text-xs">
								<span className="bg-card px-2 text-muted-foreground">
									{t({ id: "orDivider", message: "Or continue with email" })}
								</span>
							</div>
						</div>

						<form onSubmit={handleSubmit} className="space-y-4">
							<div className="space-y-2">
								<Label htmlFor="email">
									{t({ id: "email", message: "Email" })}
								</Label>
								<Input
									id="email"
									type="email"
									dir="ltr"
									className="text-left"
									autoComplete="email"
									placeholder={t({
										id: "enterEmail",
										message: "Enter your email",
									})}
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									required
								/>
							</div>
							<div className="space-y-2">
								<div className="flex items-center justify-between">
									<Label htmlFor="password">
										{t({ id: "password", message: "Password" })}
									</Label>
									<button
										type="button"
										className="text-xs font-semibold text-accent-ink underline-offset-4 hover:underline"
										onClick={() => {
											setForgotEmail(email);
											setShowForgotModal(true);
										}}
									>
										{t({
											id: "forgotPasswordLink",
											message: "Forgot password?",
										})}
									</button>
								</div>
								<Input
									id="password"
									type="password"
									dir="ltr"
									className="text-left"
									autoComplete="current-password"
									placeholder={t({
										id: "enterPassword",
										message: "Enter your password",
									})}
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									required
								/>
							</div>
							<Button type="submit" className="w-full" disabled={isLoading}>
								{isLoading
									? t({ id: "signingIn", message: "Signing in..." })
									: t({ id: "signIn", message: "Sign In" })}
							</Button>
						</form>

						<div className="pt-4 border-t text-center">
							<button
								type="button"
								className="text-xs font-semibold text-accent-ink underline-offset-4 hover:underline"
								onClick={navigateToRegister}
							>
								{t({
									id: "registrationTitleDesc",
									message: "Submit your details to request access",
								})}
							</button>
						</div>
					</div>

					{error && (
						<div className="mt-4 flex items-center justify-center gap-2 rounded-md border border-destructive/30 bg-danger-tint p-3 text-sm text-danger-ink">
							<AlertCircle className="w-4 h-4 shrink-0" />
							<span>{error}</span>
						</div>
					)}

					{infoMessage && (
						<div className="mt-4 flex items-center justify-center gap-2 rounded-md border border-live/30 bg-live-tint p-3 text-sm text-foreground">
							<CheckCircle2 className="w-4 h-4 shrink-0" />
							<span>{infoMessage}</span>
						</div>
					)}
				</CardContent>
			</Card>

			{/* Account Conflict / Password Modal */}
			<Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							{t({
								id: "linkAccountDialogTitle",
								message: "Link Your Google Account",
							})}
						</DialogTitle>
						<DialogDescription>
							{t({
								id: "linkAccountDialogDesc",
								message:
									"An account with this email already exists. Please enter your account password to confirm ownership and link your Google login.",
							})}
						</DialogDescription>
					</DialogHeader>
					<form onSubmit={handleLinkAccountSubmit} className="space-y-4 py-2">
						<div className="space-y-2">
							<Label>{t({ id: "email", message: "Email" })}</Label>
							<Input
								value={pendingEmail}
								dir="ltr"
								className="text-left"
								disabled
							/>
						</div>
						<div className="space-y-2">
							<div className="flex items-center justify-between">
								<Label htmlFor="linkPassword">
									{t({ id: "password", message: "Password" })}
								</Label>
								<button
									type="button"
									className="text-xs font-semibold text-accent-ink underline-offset-4 hover:underline"
									onClick={() => {
										setShowLinkModal(false);
										setForgotEmail(pendingEmail);
										setShowForgotModal(true);
									}}
								>
									{t({ id: "forgotPasswordLink", message: "Forgot password?" })}
								</button>
							</div>
							<Input
								id="linkPassword"
								type="password"
								dir="ltr"
								className="text-left"
								placeholder={t({
									id: "enterPassword",
									message: "Enter your password",
								})}
								value={linkPassword}
								onChange={(e) => setLinkPassword(e.target.value)}
								required
							/>
						</div>
						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => setShowLinkModal(false)}
							>
								{t({ id: "cancel", message: "Cancel" })}
							</Button>
							<Button type="submit" disabled={isLinking}>
								{isLinking ? (
									<Loader2 className="w-4 h-4 animate-spin mr-2" />
								) : null}
								{t({
									id: "confirmAndLinkBtn",
									message: "Confirm & Link Account",
								})}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>

			{/* Forgot Password Modal */}
			<Dialog open={showForgotModal} onOpenChange={setShowForgotModal}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							{t({ id: "forgotPasswordTitle", message: "Reset Your Password" })}
						</DialogTitle>
						<DialogDescription>
							{t({
								id: "forgotPasswordDesc",
								message:
									"Enter your email address and we will send you a link to reset your password.",
							})}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-2">
						<div className="space-y-2">
							<Label htmlFor="forgotEmail">
								{t({ id: "email", message: "Email" })}
							</Label>
							<Input
								id="forgotEmail"
								type="email"
								dir="ltr"
								className="text-left"
								placeholder={t({
									id: "enterEmail",
									message: "Enter your email",
								})}
								value={forgotEmail}
								onChange={(e) => setForgotEmail(e.target.value)}
								required
							/>
						</div>
					</div>
					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => setShowForgotModal(false)}
						>
							{t({ id: "cancel", message: "Cancel" })}
						</Button>
						<Button
							type="button"
							onClick={() => handleSendForgotPassword(forgotEmail)}
							disabled={isSendingReset || !forgotEmail}
						>
							{isSendingReset ? (
								<Loader2 className="w-4 h-4 animate-spin mr-2" />
							) : null}
							{t({ id: "sendResetLinkBtn", message: "Send Reset Link" })}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
